import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import AuthImage from "@/components/AuthImage";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Bookmark, BookmarkCheck, Trash2, Flag, Users, Clock, Sparkles, Printer, Share2, ChefHat, AlertOctagon, ImagePlus, X, ChevronLeft, ChevronRight, Check, Pencil, AlertTriangle, Home, MapPin, BookOpen, ShoppingBag } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import ShareSheet from "@/components/ShareSheet";
import CookMode from "@/components/CookMode";
import FolderPicker from "@/components/FolderPicker";
import { toast } from "sonner";
import { StillTruePill } from "@/components/StillTruePill";
import FieldNoteForm from "@/components/FieldNoteForm";
import Markdown from "@/components/Markdown";
import RecipeContext from "@/components/RecipeContext";
import { ShopThis } from "@/components/ShopThis";
import IdeaEditPanel from "@/components/IdeaEditPanel";
import SeedActionRow from "@/components/SeedActionRow";
import CommentThread from "@/components/CommentThread";

export default function IdeaDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [idea, setIdea] = useState(null);
  const [notes, setNotes] = useState([]);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [cookOpen, setCookOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [folderId, setFolderId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deletingBusy, setDeletingBusy] = useState(false);
  // Field Notes are collapsed by default now (Feb 22 layout revision) —
  // most visitors don't need the full list, but the summary card still
  // signals health. Author of the seed sees them expanded automatically
  // (below, after `idea` loads).
  const [fieldNotesExpanded, setFieldNotesExpanded] = useState(false);
  const [fieldNoteFormOpen, setFieldNoteFormOpen] = useState(false);
  // Authors with an empty Shop This see a small pill in the utility strip
  // that reveals an inline ShopThis editor here on the page — no need to
  // hunt for the sidebar (which is hidden until items exist).
  const [authorShopThisOpen, setAuthorShopThisOpen] = useState(false);
  // Warm-glow pulse on the hero image when the viewer taps Sun.
  const [sunGlow, setSunGlow] = useState(false);
  const sunGlowTimerRef = useRef(null);
  // Signal bumped whenever the Comment icon is tapped in the action row —
  // CommentThread listens for changes and scrolls + focuses the composer.
  const [commentFocusSignal, setCommentFocusSignal] = useState(0);
  const flashSunGlow = () => {
    setSunGlow(true);
    if (sunGlowTimerRef.current) clearTimeout(sunGlowTimerRef.current);
    // Match the willoah-sun-pulse keyframe duration (1200ms) so the overlay
    // fades in sync with the box-shadow pulse.
    sunGlowTimerRef.current = setTimeout(() => setSunGlow(false), 1200);
  };
  useEffect(() => () => {
    if (sunGlowTimerRef.current) clearTimeout(sunGlowTimerRef.current);
  }, []);

  const load = async () => {
    const [ideaRes, notesRes] = await Promise.all([
      api.get(`/ideas/${id}`),
      api.get(`/ideas/${id}/field-notes`),
    ]);
    setIdea(ideaRes.data);
    setNotes(notesRes.data);
    // Author of the seed cares about Field Notes (their audience wrote
    // them) — auto-expand so they see them without needing an extra tap.
    if (user && ideaRes.data.author_id === user.user_id) {
      setFieldNotesExpanded(true);
    }
    if (user) {
      try {
        const r = await api.get(`/ideas/${id}/saved`);
        setSaved(r.data.saved);
        setFolderId(r.data.folder_id || null);
      } catch {}
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, user?.user_id]);

  // Update the browser tab + description when a branch is viewed so a copied link
  // and browser history look clean. Real social crawlers still fetch the raw HTML.
  useEffect(() => {
    if (!idea) return;
    const prevTitle = document.title;
    const prevDesc = document.querySelector('meta[name="description"]')?.getAttribute("content");
    document.title = `${idea.title} — Willoah`;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", `${idea.title} — a real recommendation from ${idea.author_name} on Willoah. Real people. No algorithm.`);
    return () => {
      document.title = prevTitle;
      if (desc && prevDesc != null) desc.setAttribute("content", prevDesc);
    };
  }, [idea]);

  const toggleSave = async () => {
    if (!user) { toast("Sign in to save a seed."); return; }
    try {
      if (saved) {
        await api.delete(`/ideas/${id}/save`);
        setSaved(false);
        setFolderId(null);
        toast("Removed from your saved list.");
      } else {
        // Open the folder picker so filing is one flow with saving.
        setPickerOpen(true);
      }
    } catch {
      toast("Something went wrong.");
    }
  };

  const removeNote = async (note) => {
    const isAuthor = user && note.author_id === user.user_id;
    let reason = null;
    if (!isAuthor) {
      reason = window.prompt("Owner moderation: describe the abuse/harassment. (Merely-negative reviews cannot be removed.)");
      if (!reason || !reason.trim()) return;
    }
    try {
      await api.delete(`/field-notes/${note.id}`, { params: reason ? { reason } : {} });
      setNotes((prev) => prev.filter((n) => n.id !== note.id));
      toast("Field Note removed.");
    } catch (e) {
      toast(e?.response?.data?.detail || "Cannot delete this note.");
    }
  };

  const reportNote = async (note) => {
    if (!user) return toast("Sign in to flag a Field Note.");
    const picked = window.prompt(
      "Flag this Field Note for the site owner.\n\n" +
      "Pick a reason (1-4):\n" +
      "  1. Medical claim / treatment advice\n" +
      "  2. Spam or promotional post\n" +
      "  3. Personal attack or harassment\n" +
      "  4. Other (needs a short detail)\n\n" +
      "A merely-negative review is not a reason to flag."
    );
    if (!picked) return;
    const map = { "1": "medical_claim", "2": "spam", "3": "personal_attack", "4": "other" };
    const preset = map[picked.trim()];
    if (!preset) return toast("Pick 1, 2, 3, or 4.");
    const needsDetails = preset === "other";
    const details = window.prompt(
      needsDetails
        ? "Add a short detail (required for 'other')."
        : "Optional: add a short detail (or leave blank)."
    );
    if (needsDetails && (!details || !details.trim())) {
      return toast("'Other' needs a short detail.");
    }
    try {
      const { data } = await api.post(`/field-notes/${note.id}/report`, {
        reason_preset: preset,
        details: (details || "").trim() || null,
      });
      toast(data.already ? "You've already flagged this Field Note." : "Sent to the owner for review.");
    } catch (e) {
      toast(e?.response?.data?.detail || "Could not send flag.");
    }
  };

  const deleteIdea = async () => {
    if (!idea) return;
    setDeletingBusy(true);
    try {
      await api.delete(`/ideas/${idea.id}`);
      toast("Seed deleted.");
      window.location.href = "/grove";
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast(typeof d === "string" ? d : "Could not delete this seed.");
      setDeletingBusy(false);
      setConfirmingDelete(false);
    }
  };

  if (loading || !idea) {
    return <div className="max-w-6xl mx-auto px-6 pt-20 text-[#2A2823]/60">Loading seed…</div>;
  }

  // Most seeds won't have Shop This filled in — Feb 22 layout revision
  // treats "no Shop This" as the DEFAULT layout, not an edge case. When the
  // sidebar has nothing to show and the viewer isn't the author (who could
  // add items), we collapse the sidebar and let the main content fill the
  // remaining columns for a calmer, tighter page.
  const isAuthor = !!(user && idea.author_id === user.user_id);
  const hasShopItems = (idea.shop_items?.length ?? 0) > 0;
  // Show the Shop This surface only when there's actually something to
  // show. Authors with an empty Shop This get a tighter layout too and can
  // reveal an inline editor via the "Add to Shop This" pill in the utility
  // strip (see below).
  const showShopThis = hasShopItems;
  const contentColSpan = showShopThis ? "lg:col-span-5" : "lg:col-span-8";

  return (
    <main className="max-w-6xl mx-auto px-6 pt-10 pb-24">
      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-6 lg:col-span-4">
          <div className="md:sticky md:top-28">
            <div
              data-testid="idea-hero-wrapper"
              className={`relative transition-shadow duration-700 ease-out ${
                sunGlow ? "willoah-sun-glow" : ""
              }`}
            >
              <IdeaPhotoGallery idea={idea} />
              {/* Warm-glow overlay — a soft golden veil that fades in and out
                  when the viewer taps Sun. Sits absolutely over the gallery,
                  pointer-events:none so it never intercepts clicks. Kelly's
                  Feb 22 revision — reach and opacity boosted so it reads as
                  a genuine light wash, not a hairline. */}
              <div
                aria-hidden="true"
                className={`pointer-events-none absolute inset-0 rounded-3xl transition-opacity duration-700 ease-out ${
                  sunGlow ? "opacity-100" : "opacity-0"
                }`}
                style={{
                  background:
                    "radial-gradient(circle at 30% 25%, rgba(228,192,120,0.85) 0%, rgba(228,192,120,0.45) 45%, rgba(228,192,120,0.15) 80%, rgba(228,192,120,0) 100%)",
                  mixBlendMode: "screen",
                }}
              />
            </div>
          </div>
        </div>
        <div className={`md:col-span-6 ${contentColSpan}`}>          <div className="flex items-center gap-3 flex-wrap">
            <span data-testid="idea-category" className="accent-label px-3 py-1 rounded-full bg-[#B79256]/15 text-[#8B6E3F]">{idea.category}</span>
            <span className="accent-label text-[#2A2823]/50">{new Date(idea.created_at).toLocaleDateString()}</span>
            {/* Edited pill removed per Feb 22 spec — the author can silently
                update their seed (especially recipe steps) without a public
                "last edited" stamp. */}
            {user && idea.author_id === user.user_id && !editing && (
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  data-testid="edit-idea-btn"
                  onClick={() => setEditing(true)}
                  aria-label="Edit this seed"
                  title="Edit this seed"
                  className="inline-flex items-center gap-1 rounded-full px-3 py-1 border border-[#B79256]/40 bg-transparent text-[#2A2823] hover:bg-[#B79256]/10 text-xs transition-colors"
                >
                  <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} /> Edit
                </button>
                <button
                  type="button"
                  data-testid="delete-idea-btn"
                  onClick={() => setConfirmingDelete(true)}
                  aria-label="Delete this seed"
                  title="Delete this seed"
                  className="inline-flex items-center gap-1 rounded-full px-3 py-1 border border-[#B24A2A]/40 bg-transparent text-[#B24A2A] hover:bg-[#B24A2A]/10 text-xs transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} /> Delete
                </button>
              </div>
            )}
          </div>

          {/* Delete confirm — inline (not a portal) so screen readers pick
              it up as part of the branch's own controls. Two-step confirm
              stops accidental deletes on a phone. */}
          {confirmingDelete && (
            <div
              data-testid="delete-confirm-panel"
              className="mt-5 rounded-2xl border border-[#B24A2A]/40 bg-[#B24A2A]/8 p-5 space-y-3"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-[#B24A2A] mt-0.5 shrink-0" strokeWidth={2} />
                <div className="text-sm text-[#2A2823]/85 leading-relaxed">
                  <b>Delete "{idea.title}"?</b> This removes the seed, every Field Note left on it, and every save that pointed to it. Folder covers pointing here will be cleared. This can't be undone.
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  data-testid="delete-idea-confirm"
                  onClick={deleteIdea}
                  disabled={deletingBusy}
                  className="rounded-full h-10 px-5 bg-[#B24A2A] text-[#FBF8F1] hover:bg-[#963e22] shadow-none"
                >
                  <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.75}/>{deletingBusy ? "Deleting…" : "Yes, delete"}
                </Button>
                <button
                  type="button"
                  data-testid="delete-idea-cancel"
                  onClick={() => setConfirmingDelete(false)}
                  className="text-xs text-[#2A2823]/60 hover:text-[#2A2823] px-2"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          {idea.frozen && (
            <div
              data-testid="branch-frozen-banner"
              className="mt-5 rounded-2xl border border-[#7A5A2E]/40 bg-[#7A5A2E]/10 p-4 flex items-start gap-3"
            >
              <AlertOctagon className="w-4 h-4 text-[#7A5A2E] mt-0.5 shrink-0" strokeWidth={2} />
              <div className="text-sm text-[#2A2823]/85">
                <b>This seed is temporarily paused.</b>
                <br />
                Someone flagged it and it's hidden from The Grove while we take a
                look. If nothing's amiss, it'll be back within a day or two.
              </div>
            </div>
          )}
          {!editing && (
            <h1 data-testid="idea-title" className="font-serif-display text-5xl sm:text-6xl text-[#2A2823] mt-4 leading-[1.05]">{idea.title}</h1>
          )}
          {editing && (
            <div className="mt-4">
              <IdeaEditPanel
                idea={idea}
                onCancel={() => setEditing(false)}
                onSaved={(updated) => { setIdea((prev) => ({ ...prev, ...updated })); setEditing(false); }}
              />
            </div>
          )}
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <p className="text-[#2A2823]/60">
              by <Link to={`/roots/${idea.author_id}`} className="text-[#4E5A3D] hover:underline">{idea.author_name}</Link>
            </p>
            <StillTruePill stillTrue={idea.still_true} />
          </div>

          {idea.still_true?.last_status === "no" && (
            <div
              data-testid="still-true-no-banner"
              className="mt-5 rounded-2xl bg-[#B24A2A]/8 border border-[#B24A2A]/30 p-4 flex items-start gap-3"
            >
              <AlertOctagon className="w-5 h-5 text-[#B24A2A] mt-0.5 shrink-0" strokeWidth={2} />
              <div className="text-sm text-[#2A2823]/85 leading-relaxed">
                <b>The person who planted this no longer stands by it.</b>
                {idea.still_true.no_note ? (
                  <>
                    {" "}They added:{" "}
                    <span className="italic">&ldquo;{idea.still_true.no_note}&rdquo;</span>
                  </>
                ) : null}
                {" "}The original post is kept up so the change stays honest, not hidden.
              </div>
            </div>
          )}

          {user && idea.author_id === user.user_id && idea.still_true?.due_for_check && (
            <StillTruePrompt
              ideaId={idea.id}
              onDone={(updated) => setIdea((prev) => ({ ...prev, ...updated }))}
            />
          )}

          {idea.category === "Trusted" && (
            <div data-testid="trusted-strip" className="mt-5 rounded-2xl bg-[#4E5A3D]/8 border border-[#4E5A3D]/25 p-4 space-y-2">
              {idea.provider_type && (
                <div className="text-sm text-[#2A2823]">
                  <span className="accent-label text-[#4E5A3D] mr-2">type</span>{idea.provider_type}
                </div>
              )}
              {idea.service_area && (
                <div className="text-sm text-[#2A2823]">
                  <span className="accent-label text-[#4E5A3D] mr-2">serves</span>{idea.service_area}
                </div>
              )}
              {idea.website_url && (
                <div className="text-sm">
                  <a
                    href={idea.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="trusted-website"
                    className="inline-flex items-center gap-1.5 text-[#4E5A3D] hover:underline"
                  >
                    Visit website
                  </a>
                  <span className="ml-2 text-xs text-[#2A2823]/50">informational · not an affiliate link</span>
                </div>
              )}
            </div>
          )}
          {idea.category === "Trusted" && user && idea.author_id !== user.user_id && (
            <TrustedReportButton ideaId={idea.id} />
          )}

          {/* Category-specific info strips — Home / Travel/Places / Faith */}
          {idea.category === "Home" && idea.room_space && (
            <div data-testid="home-strip" className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#B79256]/12 border border-[#B79256]/30 px-5 py-2.5">
              <Home className="w-4 h-4 text-[#7A5A2E]" strokeWidth={1.75} />
              <span className="text-sm text-[#2A2823]">
                <span className="accent-label text-[#7A5A2E] mr-2">room</span>
                <b className="font-serif-display text-lg text-[#7A5A2E]">{idea.room_space}</b>
              </span>
            </div>
          )}
          {idea.category === "Travel/Places" && idea.place_location && (
            <div data-testid="travel-strip" className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#4E5A3D]/12 border border-[#4E5A3D]/30 px-5 py-2.5">
              <MapPin className="w-4 h-4 text-[#4E5A3D]" strokeWidth={1.75} />
              <span className="text-sm text-[#2A2823]">
                <span className="accent-label text-[#4E5A3D] mr-2">where</span>
                <b className="font-serif-display text-lg text-[#4E5A3D]">{idea.place_location}</b>
              </span>
            </div>
          )}
          {idea.category === "Faith" && idea.scripture_ref && (
            <div data-testid="faith-strip" className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#4E5A3D]/10 border border-[#4E5A3D]/25 px-5 py-2.5">
              <BookOpen className="w-4 h-4 text-[#3E4830]" strokeWidth={1.75} />
              <span className="text-sm text-[#2A2823]">
                <span className="accent-label text-[#3E4830] mr-2">scripture</span>
                <b className="font-serif-display text-lg text-[#3E4830]">{idea.scripture_ref}</b>
              </span>
            </div>
          )}

          {idea.category === "Recipes" && (idea.servings || idea.total_time) && (
            <div data-testid="recipe-strip" className="mt-5 inline-flex flex-wrap items-center gap-4 rounded-full bg-[#B79256]/12 border border-[#B79256]/25 px-5 py-2.5">
              {idea.servings && (
                <span className="inline-flex items-center gap-2 text-sm text-[#2A2823]">
                  <Users className="w-4 h-4 text-[#4E5A3D]" strokeWidth={1.75}/>
                  <span><b className="font-serif-display text-lg text-[#4E5A3D]">{idea.servings}</b> <span className="accent-label text-[#B79256] ml-1">servings</span></span>
                </span>
              )}
              {idea.servings && idea.total_time && <span className="w-px h-4 bg-[#B79256]/40" />}
              {idea.total_time && (
                <span className="inline-flex items-center gap-2 text-sm text-[#2A2823]">
                  <Clock className="w-4 h-4 text-[#4E5A3D]" strokeWidth={1.75}/>
                  <span><b className="font-serif-display text-lg text-[#4E5A3D]">{idea.total_time}</b> <span className="accent-label text-[#B79256] ml-1">total</span></span>
                </span>
              )}
            </div>
          )}

          {idea.recent_saves_count > 0 && (
            <div data-testid="save-streak" className="mt-4 inline-flex items-center gap-2 text-sm text-[#4E5A3D]">
              <Sparkles className="w-4 h-4" strokeWidth={1.75}/>
              <span>
                <b>{idea.recent_saves_count}</b> {idea.recent_saves_count === 1 ? "person" : "people"} saved this seed this week
              </span>
            </div>
          )}

          {!editing && (
          <div className="mt-8 rounded-2xl border border-[#B79256]/30 bg-white/60 p-5">
            <div className="accent-label text-[#B79256]">proof of use</div>
            <p className="font-serif-display text-2xl text-[#4E5A3D] mt-2">{idea.proof_label}</p>
          </div>
          )}

          {!editing && (
          <div className="mt-8">
            <div className="accent-label text-[#2A2823]/50">context</div>
            <div className="mt-3 text-lg">
              <RecipeContext context={idea.context} category={idea.category} title={idea.title} idea={idea} />
            </div>
          </div>
          )}

          <SeedActionRow
            idea={idea}
            saved={saved}
            onToggleSave={toggleSave}
            onShare={() => setShareOpen(true)}
            onCommentJump={() => setCommentFocusSignal((n) => n + 1)}
            onSunTap={flashSunGlow}
          />

          {/* IG-style lightweight comments — separate primitive from the
              structured Field Notes below. Tapping the comment icon in the
              action row bumps `commentFocusSignal` which scrolls + focuses
              the composer. */}
          <CommentThread
            ideaId={idea.id}
            seedAuthorId={idea.author_id}
            openSignal={commentFocusSignal}
          />
          {/* Utility strip — Cook Mode + Print + (author-only) Add to Shop
              This grouped in a small, muted right-aligned row. Feb 22
              layout revision: Shop This sidebar is hidden when there's
              nothing to shop; the author can reveal an inline editor here
              via the pill so the tighter layout still supports content
              editing. */}
          <div className="mt-3 flex flex-wrap items-center justify-end gap-2 no-print">
            {isAuthor && !hasShopItems && (
              <button
                type="button"
                data-testid="add-shop-this-btn"
                onClick={() => setAuthorShopThisOpen((v) => !v)}
                aria-expanded={authorShopThisOpen}
                className={`inline-flex items-center gap-1.5 rounded-full h-8 px-3 border text-xs font-serif-display italic transition-colors ${
                  authorShopThisOpen
                    ? "border-[#4E5A3D] bg-[#4E5A3D]/10 text-[#4E5A3D]"
                    : "border-[#B79256]/40 bg-transparent text-[#8B6E3F] hover:bg-[#B79256]/10"
                }`}
                title="Add shoppable items to this seed"
              >
                <ShoppingBag className="w-3.5 h-3.5" strokeWidth={1.75}/>
                {authorShopThisOpen ? "close" : "add to shop this"}
              </button>
            )}
            {idea.category === "Recipes" && /##\s+(steps|instructions|method|directions)/i.test(idea.context || "") && (
              <button
                type="button"
                data-testid="cook-mode-btn"
                onClick={() => setCookOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-full h-8 px-3 border border-[#B79256]/40 bg-transparent text-[#8B6E3F] hover:bg-[#B79256]/10 text-xs font-serif-display italic transition-colors"
                title="Hands-free cook mode — one step at a time"
              >
                <ChefHat className="w-3.5 h-3.5" strokeWidth={1.75}/>cook mode
              </button>
            )}
            <button
              type="button"
              data-testid="print-btn"
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 rounded-full h-8 px-3 border border-[#B79256]/40 bg-transparent text-[#2A2823]/60 hover:bg-[#B79256]/10 hover:text-[#2A2823] text-xs font-serif-display italic transition-colors"
              title="Print this seed"
            >
              <Printer className="w-3.5 h-3.5" strokeWidth={1.75}/>print
            </button>
          </div>

          {/* Author-triggered inline Shop This editor — only rendered when
              the author has tapped the pill above. Keeps the default page
              tight while giving them a place to add items without hunting
              for a hidden sidebar. */}
          {isAuthor && authorShopThisOpen && !hasShopItems && (
            <div data-testid="author-shop-this-inline" className="mt-4">
              <ShopThis
                idea={idea}
                isAuthor={true}
                onIdeaUpdated={setIdea}
                variant="inline"
              />
            </div>
          )}

          {/* Shop This — inline on mobile/tablet, before Field Notes.
              Desktop viewers see the sticky sidebar version to the right.
              Skipped entirely when there are no items AND the viewer isn't
              the author (Feb 22 layout revision — most seeds don't have
              this section filled in). */}
          {showShopThis && (
            <div className="lg:hidden mt-8">
              <ShopThis
                idea={idea}
                isAuthor={isAuthor}
                onIdeaUpdated={setIdea}
                variant="inline"
              />
            </div>
          )}

          <section className="mt-10">
            <button
              type="button"
              data-testid="field-notes-toggle"
              onClick={() => setFieldNotesExpanded((v) => !v)}
              className="w-full flex items-baseline justify-between gap-3 text-left group"
              aria-expanded={fieldNotesExpanded}
            >
              <div className="flex items-baseline gap-3">
                <h2 data-testid="field-notes-heading" className="font-serif-display text-2xl sm:text-3xl text-[#2A2823] scroll-mt-24">Field Notes</h2>
                <span className="accent-label text-[#2A2823]/50">{notes.length} from real users</span>
              </div>
              <span className="accent-label text-[#B79256] group-hover:text-[#8B6E3F] transition-colors">
                {fieldNotesExpanded ? "hide" : (notes.length ? "show" : "write one")}
              </span>
            </button>
            {!fieldNotesExpanded && notes.length === 0 && (
              <p data-testid="field-notes-empty-hint" className="text-sm text-[#2A2823]/55 mt-2 italic">
                Structured reviews from people who saved this seed. No stars. No fluff.
              </p>
            )}
            {/* Preview peek — when collapsed and there IS at least one note,
                surface the newest one as a subtle teaser so the section
                feels alive without needing to be expanded. Tapping it opens
                the full list. Feb 22 revision. */}
            {!fieldNotesExpanded && notes.length > 0 && (
              <button
                type="button"
                data-testid="field-notes-peek"
                onClick={() => setFieldNotesExpanded(true)}
                className="mt-3 w-full text-left rounded-2xl border border-[#B79256]/25 bg-white/50 hover:bg-white/80 px-4 py-3 transition-colors"
                title="Show all field notes"
              >
                <div className="flex items-start gap-3">
                  {/* Small round avatar — the reviewer's face makes the peek
                      read as a real person's voice, not a stat. Falls back
                      to a lettered coin when no picture is available. */}
                  {notes[0].author_custom_picture_path ? (
                    <AuthImage
                      path={notes[0].author_custom_picture_path}
                      alt=""
                      className="w-9 h-9 rounded-full object-cover shrink-0 border border-[#B79256]/30"
                    />
                  ) : notes[0].author_picture ? (
                    <img
                      src={notes[0].author_picture}
                      alt=""
                      referrerPolicy="no-referrer"
                      className="w-9 h-9 rounded-full object-cover shrink-0 border border-[#B79256]/30"
                    />
                  ) : (
                    <div
                      className="w-9 h-9 rounded-full bg-[#4E5A3D]/15 text-[#4E5A3D] flex items-center justify-center text-sm font-medium shrink-0"
                      aria-hidden="true"
                    >
                      {(notes[0].author_name || "?").slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-[#2A2823]/85 leading-snug">
                      <span className="font-medium text-[#4E5A3D]">{notes[0].author_name}</span>
                      {notes[0].would_do_again ? (
                        <span className="accent-label text-[10px] text-[#4E5A3D] ml-2">would do it again</span>
                      ) : (
                        <span className="accent-label text-[10px] text-[#2A2823]/50 ml-2">would not</span>
                      )}
                    </p>
                    <p className="text-sm text-[#2A2823]/75 italic mt-1 line-clamp-2 break-words">
                      &ldquo;{notes[0].would_do_again_reason || notes[0].downside || notes[0].not_good_for || "…"}&rdquo;
                    </p>
                    {notes.length > 1 && (
                      <p className="text-[11px] text-[#B79256] mt-1.5">
                        + {notes.length - 1} more · tap to read
                      </p>
                    )}
                  </div>
                </div>
              </button>
            )}

            {notes.length > 0 && (() => {
              const yes = notes.filter((n) => n.would_do_again).length;
              const no = notes.length - yes;
              const pct = Math.round((yes / notes.length) * 100);
              return (
                <div data-testid="field-notes-summary" className="mt-4 rounded-2xl border border-[#B79256]/30 bg-white/70 p-4 flex flex-wrap items-center gap-x-6 gap-y-2">
                  <div className="flex items-baseline gap-2">
                    <span className="font-serif-display text-3xl text-[#4E5A3D]" data-testid="fn-summary-pct">{pct}%</span>
                    <span className="text-sm text-[#2A2823]/70">would do it again</span>
                  </div>
                  <div className="h-4 w-px bg-[#B79256]/40 hidden sm:block" />
                  <div className="flex items-center gap-4 text-sm text-[#2A2823]/70">
                    <span data-testid="fn-summary-yes"><b className="text-[#4E5A3D]">{yes}</b> said would again</span>
                    <span data-testid="fn-summary-no"><b className="text-[#2A2823]">{no}</b> said would not</span>
                  </div>
                  <div className="flex-1 min-w-[140px] h-2 rounded-full bg-[#B79256]/15 overflow-hidden ml-auto">
                    <div className="h-full bg-[#4E5A3D] transition-[width] duration-500" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })()}

            {fieldNotesExpanded && (
              <>
                {user && (
                  <div className="no-print mt-6">
                    <FieldNoteForm
                      ideaId={idea.id}
                      saved={saved}
                      category={idea.category}
                      onCreated={(n) => setNotes((prev) => [n, ...prev])}
                    />
                  </div>
                )}

                <div className="mt-6 space-y-6">
                  {notes.length === 0 && (
                    <p data-testid="empty-field-notes" className="text-[#2A2823]/50 italic">No Field Notes yet. Save this branch and be the first to write one.</p>
                  )}
                  {notes.map((n) => (
                    <FieldNoteCard
                      key={n.id}
                      note={n}
                      user={user}
                      idea={idea}
                      onDelete={() => removeNote(n)}
                      onReport={() => reportNote(n)}
                      onUpdated={(updatedNote) =>
                        setNotes((prev) => prev.map((x) => (x.id === updatedNote.id ? updatedNote : x)))
                      }
                    />
                  ))}
                </div>
              </>
            )}
          </section>
        </div>

        {/* Desktop-only sticky Shop This sidebar. Hidden on mobile — the
            same panel renders inline above Field Notes on smaller screens.
            Skipped entirely when there are no items AND the viewer isn't
            the author (the norm for most seeds). */}
        {showShopThis && (
          <aside className="hidden lg:block lg:col-span-3">
            <div className="sticky top-24">
              <ShopThis
                idea={idea}
                isAuthor={isAuthor}
                onIdeaUpdated={setIdea}
                variant="sidebar"
              />
            </div>
          </aside>
        )}
      </div>

      {/* Print-only footer — appears at the bottom of the printed sheet */}
      <div className="print-only mt-16 pt-6 border-t border-[#B79256]/40 text-center">
        <div className="font-serif-display text-3xl text-[#2A2823]">Willoah</div>
        <div className="accent-label text-[#B79256] mt-1">real life. real ideas.</div>
        <p className="text-xs text-[#2A2823]/60 mt-3">
          Printed from Willoah — real people, no algorithm. Sponsored content is always clearly labeled on Willoah.
        </p>
      </div>

      <ShareSheet idea={idea} open={shareOpen} onClose={() => setShareOpen(false)} />
      <CookMode idea={idea} open={cookOpen} onClose={() => setCookOpen(false)} />
      <FolderPicker
        open={pickerOpen}
        ideaId={id}
        currentFolderId={folderId}
        onClose={() => setPickerOpen(false)}
        onSaved={(fid) => { setSaved(true); setFolderId(fid); }}
      />
    </main>
  );
}

function StillTruePrompt({ ideaId, onDone }) {
  const [expanded, setExpanded] = useState(false);
  const [choice, setChoice] = useState(null); // 'yes' | 'no'
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (status) => {
    setBusy(true);
    try {
      const body = { status };
      const trimmed = note.trim();
      if (trimmed) body.note = trimmed;
      const { data } = await api.post(`/ideas/${ideaId}/still-true`, body);
      toast(
        status === "yes"
          ? "Confirmed — this stays marked ‘still true’."
          : "Noted. Your update is shown on the seed.",
      );
      onDone?.(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast(typeof detail === "string" ? detail : "Could not save your check-in.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="still-true-prompt"
      className="mt-6 rounded-2xl border border-[#4E5A3D]/35 bg-[#4E5A3D]/6 p-5"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 w-7 h-7 rounded-full bg-[#4E5A3D]/15 text-[#4E5A3D] flex items-center justify-center shrink-0">
          <Check className="w-4 h-4" strokeWidth={2.25} />
        </div>
        <div className="flex-1">
          <p className="font-serif-display text-lg text-[#2A2823]">
            Do you still stand by this seed?
          </p>
          <p className="text-xs text-[#2A2823]/60 mt-1 leading-relaxed">
            It's been a while — a quick one-tap confirmation keeps this recommendation honest for people finding it later.
          </p>

          {!expanded ? (
            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                data-testid="still-true-yes"
                onClick={() => submit("yes")}
                disabled={busy}
                className="rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] h-9 px-4 text-xs shadow-none"
              >
                Yes — still true
              </Button>
              <Button
                data-testid="still-true-no-open"
                onClick={() => { setExpanded(true); setChoice("no"); }}
                disabled={busy}
                variant="outline"
                className="rounded-full h-9 px-4 text-xs border-[#B24A2A]/40 text-[#B24A2A] hover:bg-[#B24A2A]/8 hover:text-[#B24A2A] bg-transparent"
              >
                No — it didn't hold up
              </Button>
              <Button
                data-testid="still-true-yes-note-open"
                onClick={() => { setExpanded(true); setChoice("yes"); }}
                disabled={busy}
                variant="outline"
                className="rounded-full h-9 px-4 text-xs border-[#4E5A3D]/40 text-[#4E5A3D] hover:bg-[#4E5A3D]/8 hover:text-[#4E5A3D] bg-transparent"
              >
                Yes, and add a note
              </Button>
            </div>
          ) : (
            <div className="mt-4 space-y-2">
              <Textarea
                data-testid="still-true-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={280}
                placeholder={
                  choice === "yes"
                    ? "Optional — anything worth adding? (e.g. still my favourite after two summers)"
                    : "What changed? (e.g. stopped working after a year)"
                }
                className="rounded-2xl border-[#B79256]/40 bg-white text-sm"
              />
              <div className="flex justify-between items-center gap-2">
                <span className="text-[10px] text-[#2A2823]/50">{note.length}/280</span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    data-testid="still-true-cancel"
                    onClick={() => { setExpanded(false); setChoice(null); setNote(""); }}
                    className="text-xs text-[#2A2823]/60 hover:text-[#2A2823] px-2"
                  >
                    Cancel
                  </button>
                  <Button
                    data-testid="still-true-submit"
                    onClick={() => submit(choice)}
                    disabled={busy || (choice === "no" && !note.trim())}
                    className={`rounded-full h-9 px-4 text-xs shadow-none ${
                      choice === "yes"
                        ? "bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830]"
                        : "bg-[#B24A2A] text-[#FBF8F1] hover:bg-[#963e22]"
                    }`}
                  >
                    {choice === "yes" ? "Save confirmation" : "Save update"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function IdeaPhotoGallery({ idea }) {
  const photos = (idea.photos && idea.photos.length ? idea.photos : (idea.photo_path ? [idea.photo_path] : []));
  const [activeIdx, setActiveIdx] = useState(0);
  const [lightboxIdx, setLightboxIdx] = useState(-1); // -1 = closed
  useEffect(() => { setActiveIdx(0); setLightboxIdx(-1); }, [idea.id]);
  const cur = photos[activeIdx] || photos[0];
  const open = lightboxIdx >= 0;

  // Keyboard nav for the lightbox — arrows to step, Esc to close.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxIdx(-1);
      else if (e.key === "ArrowRight") setLightboxIdx((i) => (i + 1) % photos.length);
      else if (e.key === "ArrowLeft") setLightboxIdx((i) => (i - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    // Prevent background scroll while the lightbox is up.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, photos.length]);

  if (photos.length === 0) {
    return (
      <div
        data-testid="detail-no-photo"
        className="rounded-[2rem] overflow-hidden border border-[#B79256]/25 bg-gradient-to-br from-[#B79256]/12 to-[#4E5A3D]/8 aspect-[4/5] flex flex-col items-center justify-center px-8 text-center"
      >
        <div className="w-20 h-20 rounded-full bg-[#B79256]/20 flex items-center justify-center mb-6">
          <span className="font-serif-display text-3xl text-[#B79256]">{(idea.title || "?").slice(0,1).toUpperCase()}</span>
        </div>
        <div className="font-serif-display text-3xl text-[#2A2823] leading-tight">{idea.title}</div>
        {idea.provider_type && (
          <div className="accent-label text-[#4E5A3D] mt-3">{idea.provider_type}</div>
        )}
        {idea.service_area && (
          <div className="text-sm text-[#2A2823]/60 mt-1">{idea.service_area}</div>
        )}
      </div>
    );
  }

  const openLightbox = (idx) => setLightboxIdx(idx);

  return (
    <div data-testid="idea-photo-gallery">
      <button
        type="button"
        data-testid="idea-photo-hero-open"
        onClick={() => openLightbox(activeIdx)}
        className="block w-full rounded-[2rem] overflow-hidden border border-[#B79256]/25 bg-white cursor-zoom-in group relative"
        title="Tap to view full-screen"
      >
        <AuthImage
          key={cur}
          path={cur}
          alt={idea.title}
          className="w-full max-h-[75vh] object-cover group-hover:scale-[1.02] transition-transform duration-500"
        />
        {photos.length > 1 && (
          <span
            data-testid="idea-photo-hero-badge"
            className="absolute bottom-3 right-3 accent-label text-[10px] bg-[#2A2823]/70 text-[#FBF8F1] px-2.5 py-1 rounded-full backdrop-blur-sm"
          >
            {activeIdx + 1} / {photos.length} · tap to expand
          </span>
        )}
      </button>
      {photos.length > 1 && (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid="idea-photo-thumbs" role="tablist" aria-label="More photos of this branch">
          {photos.map((p, idx) => {
            const active = idx === activeIdx;
            return (
              <button
                key={`${p}-${idx}`}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={`idea-photo-thumb-${idx}`}
                onClick={() => setActiveIdx(idx)}
                onDoubleClick={() => openLightbox(idx)}
                className={`shrink-0 w-16 h-16 rounded-xl overflow-hidden border-2 transition-all ${
                  active ? "border-[#4E5A3D] ring-2 ring-[#4E5A3D]/25" : "border-[#B79256]/30 hover:border-[#4E5A3D]/60 opacity-80 hover:opacity-100"
                }`}
              >
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`}
                  alt={`angle ${idx + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            );
          })}
        </div>
      )}
      {open && (
        <PhotoLightbox
          photos={photos}
          index={lightboxIdx}
          onIndexChange={(i) => { setLightboxIdx(i); setActiveIdx(i); }}
          onClose={() => setLightboxIdx(-1)}
          alt={idea.title}
        />
      )}
    </div>
  );
}

function PhotoLightbox({ photos, index, onIndexChange, onClose, alt }) {
  const touchStartX = useRef(null);
  const touchDeltaX = useRef(0);
  const goPrev = () => onIndexChange((index - 1 + photos.length) % photos.length);
  const goNext = () => onIndexChange((index + 1) % photos.length);
  const src = `${process.env.REACT_APP_BACKEND_URL}/api/files/${photos[index]}`;

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
  };
  const onTouchMove = (e) => {
    if (touchStartX.current == null) return;
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
  };
  const onTouchEnd = () => {
    const dx = touchDeltaX.current;
    touchStartX.current = null;
    touchDeltaX.current = 0;
    if (Math.abs(dx) < 50) return;
    if (dx < 0) goNext(); else goPrev();
  };

  return (
    <div
      data-testid="idea-photo-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`Photo ${index + 1} of ${photos.length} — ${alt}`}
      onClick={onClose}
      className="fixed inset-0 z-[90] bg-black/92 backdrop-blur-sm flex items-center justify-center animate-[fadeIn_.18s_ease-out]"
    >
      {/* Counter */}
      <div
        data-testid="idea-photo-lightbox-counter"
        className="absolute top-5 left-1/2 -translate-x-1/2 accent-label text-xs text-[#FBF8F1]/80 bg-black/40 px-3 py-1 rounded-full"
        onClick={(e) => e.stopPropagation()}
      >
        {index + 1} / {photos.length}
      </div>

      {/* Close */}
      <button
        type="button"
        data-testid="idea-photo-lightbox-close"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-[#FBF8F1] flex items-center justify-center transition-colors"
        title="Close (Esc)"
        aria-label="Close photo viewer"
      >
        <X className="w-5 h-5" strokeWidth={2} />
      </button>

      {/* Image (stop-propagation so tapping it doesn't close) */}
      <div
        className="w-full h-full flex items-center justify-center px-4 sm:px-16"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <img
          key={src}
          src={src}
          alt={`${alt} — photo ${index + 1}`}
          data-testid="idea-photo-lightbox-image"
          className="max-w-full max-h-[86vh] object-contain rounded-2xl shadow-2xl select-none"
          draggable={false}
        />
      </div>

      {/* Prev / Next */}
      {photos.length > 1 && (
        <>
          <button
            type="button"
            data-testid="idea-photo-lightbox-prev"
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-[#FBF8F1] items-center justify-center transition-colors"
            title="Previous (←)"
            aria-label="Previous photo"
          >
            <ChevronLeft className="w-6 h-6" strokeWidth={2} />
          </button>
          <button
            type="button"
            data-testid="idea-photo-lightbox-next"
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-[#FBF8F1] items-center justify-center transition-colors"
            title="Next (→)"
            aria-label="Next photo"
          >
            <ChevronRight className="w-6 h-6" strokeWidth={2} />
          </button>

          {/* Bottom thumbnail strip */}
          <div
            data-testid="idea-photo-lightbox-strip"
            onClick={(e) => e.stopPropagation()}
            className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2 max-w-[92vw] overflow-x-auto px-3 py-2 rounded-full bg-black/45 backdrop-blur-sm"
          >
            {photos.map((p, i) => (
              <button
                key={`${p}-lb-${i}`}
                type="button"
                data-testid={`idea-photo-lightbox-thumb-${i}`}
                onClick={() => onIndexChange(i)}
                className={`shrink-0 w-11 h-11 rounded-lg overflow-hidden border-2 transition-all ${
                  i === index ? "border-[#FBF8F1] ring-2 ring-[#FBF8F1]/40" : "border-white/30 opacity-70 hover:opacity-100"
                }`}
                aria-label={`Jump to photo ${i + 1}`}
              >
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function FieldNoteCard({ note, user, idea, onDelete, onReport, onUpdated }) {
  const [drafting, setDrafting] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [updatePhoto, setUpdatePhoto] = useState(null); // { path, preview? }
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const updatePhotoRef = useRef(null);
  const isAuthor = user && user.user_id === note.author_id;
  const canDelete = user && (isAuthor || user.is_owner);
  const updates = note.updates || [];
  // Provider Reply was rolled back — no new replies. Any historical reply
  // stored on the note still renders for continuity.
  const providerReply = note.provider_reply || null;

  // Nudge author to add an update after their chosen cadence (default 6mo)
  const nudgeInterval = note.nudge_interval_days || 180;
  const lastActivityIso = updates.length
    ? updates[updates.length - 1].created_at
    : note.created_at;
  const daysSince = Math.floor(
    (Date.now() - new Date(lastActivityIso).getTime()) / (1000 * 60 * 60 * 24)
  );
  const showCadenceNudge = isAuthor && !drafting && daysSince >= nudgeInterval;

  const changeCadence = async (interval) => {
    try {
      const { data } = await api.patch(`/field-notes/${note.id}/cadence`, {
        interval_days: interval,
      });
      onUpdated(data);
      toast("Cadence updated.");
    } catch (e) {
      toast(e?.response?.data?.detail || "Could not update cadence.");
    }
  };

  const submitUpdate = async () => {
    const t = text.trim();
    if (!t) return toast("Add a short update.");
    setBusy(true);
    try {
      const body = { text: t };
      if (updatePhoto?.path) body.photo_path = updatePhoto.path;
      const { data } = await api.post(`/field-notes/${note.id}/updates`, body);
      onUpdated(data);
      setText("");
      setUpdatePhoto(null);
      setDrafting(false);
      toast("Update added.");
    } catch (e) {
      toast(e?.response?.data?.detail || "Could not add the update.");
    } finally {
      setBusy(false);
    }
  };

  const pickUpdatePhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const localPreview = URL.createObjectURL(f);
    setUpdatePhoto({ preview: localPreview });
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUpdatePhoto({ path: data.path, preview: localPreview });
      toast("Photo attached.");
    } catch (err) {
      toast(err?.response?.data?.detail || "Photo upload failed.");
      setUpdatePhoto(null);
    } finally {
      setUploadingPhoto(false);
      if (e.target) e.target.value = "";
    }
  };

  const removeUpdatePhoto = () => setUpdatePhoto(null);

  return (
    <article data-testid={`field-note-${note.id}`} className="rounded-2xl border border-[#B79256]/25 bg-white p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-serif-display text-xl text-[#2A2823]">{note.author_name}</p>
          <p className="accent-label text-[#2A2823]/50 mt-0.5">{new Date(note.created_at).toLocaleDateString()}</p>
        </div>
        <span className={`accent-label px-3 py-1 rounded-full ${note.would_do_again ? "bg-[#4E5A3D]/10 text-[#4E5A3D]" : "bg-[#2A2823]/10 text-[#2A2823]/80"}`}>
          {note.would_do_again ? "would again" : "would not again"}
        </span>
      </div>
      <dl className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
        <div><dt className="accent-label text-[#B79256]">how long</dt><dd className="mt-1 text-[#2A2823]/85">{note.duration}</dd></div>
        <div><dt className="accent-label text-[#B79256]">what paid</dt><dd className="mt-1 text-[#2A2823]/85">{note.price_paid}</dd></div>
        <div className="sm:col-span-2"><dt className="accent-label text-[#B79256]">why / why not</dt><dd className="mt-1 text-[#2A2823]/85">{note.would_do_again_reason}</dd></div>
        <div><dt className="accent-label text-[#B79256]">the downside</dt><dd className="mt-1 text-[#2A2823]/85">{note.downside}</dd></div>
        <div><dt className="accent-label text-[#B79256]">not good for</dt><dd className="mt-1 text-[#2A2823]/85">{note.not_good_for}</dd></div>
      </dl>

      {updates.length > 0 && (
        <div data-testid={`field-note-updates-${note.id}`} className="mt-6 space-y-4 border-l-2 border-[#4E5A3D]/25 pl-4">
          <div className="accent-label text-[#4E5A3D]">updates from the sharer</div>
          {updates.map((u, idx) => (
            <div key={idx} data-testid={`field-note-update-${note.id}-${idx}`} className="text-sm text-[#2A2823]/85">
              <div className="accent-label text-[#2A2823]/45 mb-1">
                {new Date(u.created_at).toLocaleDateString()}
              </div>
              <p className="whitespace-pre-wrap">{u.text}</p>
              {u.photo_path && (
                <button
                  type="button"
                  data-testid={`field-note-update-photo-${note.id}-${idx}`}
                  onClick={() => setLightboxSrc(`${process.env.REACT_APP_BACKEND_URL}/api/files/${u.photo_path}`)}
                  className="mt-2 block rounded-2xl overflow-hidden border border-[#B79256]/30 hover:border-[#4E5A3D] transition-colors max-w-[220px]"
                  title="Tap to enlarge"
                >
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${u.photo_path}`}
                    alt="Update photo"
                    className="w-full h-auto object-cover"
                  />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {providerReply && (
        <div
          data-testid={`provider-reply-${note.id}`}
          className="mt-6 ml-4 rounded-2xl bg-[#B79256]/8 border-l-2 border-[#B79256]/50 px-4 py-3"
        >
          <div className="flex items-baseline gap-2">
            <span className="accent-label text-[#B79256]">reply from the provider</span>
            <span className="accent-label text-[#2A2823]/45">
              · {providerReply.author_name} · {new Date(providerReply.created_at).toLocaleDateString()}
            </span>
          </div>
          <p className="mt-2 text-sm text-[#2A2823]/85 whitespace-pre-wrap">{providerReply.text}</p>
          <p className="mt-2 text-xs text-[#2A2823]/40 italic">
            Replies can't be edited. The original Field Note above is the sharer's honest review.
          </p>
        </div>
      )}

      {isAuthor && (
        <div className="mt-5 no-print">
          {showCadenceNudge && (
            <p
              data-testid={`cadence-nudge-${note.id}`}
              className="mb-3 text-sm text-[#4E5A3D] bg-[#4E5A3D]/8 rounded-2xl px-4 py-2.5 inline-block"
            >
              It's been about {Math.round(daysSince / 30)} months since your last check-in — how's it holding up?
            </p>
          )}
          {!drafting ? (
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                data-testid={`add-update-open-${note.id}`}
                onClick={() => setDrafting(true)}
                className="inline-flex items-center gap-1.5 accent-label text-[#4E5A3D] hover:text-[#3E4830] transition-colors"
                title="Append a dated update to this Field Note — original review stays intact"
              >
                + Add an update
              </button>
              <span className="text-xs text-[#2A2823]/45">·</span>
              <div className="inline-flex items-center gap-1.5">
                <span className="text-xs text-[#2A2823]/55">Remind me every</span>
                {[90, 180, 365].map((d) => (
                  <button
                    key={d}
                    type="button"
                    data-testid={`cadence-${d}-${note.id}`}
                    onClick={() => changeCadence(d)}
                    className={`text-xs rounded-full px-2.5 py-0.5 border transition-colors ${
                      nudgeInterval === d
                        ? "bg-[#4E5A3D] text-[#FBF8F1] border-[#4E5A3D]"
                        : "bg-transparent text-[#2A2823]/60 border-[#B79256]/40 hover:border-[#4E5A3D]"
                    }`}
                    title={`Nudge me to check in after ${d === 90 ? "3 months" : d === 180 ? "6 months" : "12 months"}`}
                  >
                    {d === 90 ? "3mo" : d === 180 ? "6mo" : "12mo"}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-[#2A2823]/55">
                Your original review stays as-is. New updates appear below it, timestamped.
              </p>
              <Textarea
                data-testid={`add-update-textarea-${note.id}`}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="How's it holding up? What's changed since you first wrote this?"
                className="rounded-2xl border-[#B79256]/40 bg-[#FBF8F1] text-sm"
                maxLength={1000}
              />
              {updatePhoto ? (
                <div
                  data-testid={`add-update-photo-preview-${note.id}`}
                  className="relative inline-block rounded-2xl overflow-hidden border border-[#B79256]/30 max-w-[180px]"
                >
                  <img src={updatePhoto.preview} alt="attached preview" className="w-full h-auto object-cover" />
                  {uploadingPhoto && (
                    <div className="absolute inset-0 bg-black/40 text-[#FBF8F1] flex items-center justify-center text-xs accent-label">
                      uploading…
                    </div>
                  )}
                  <button
                    type="button"
                    data-testid={`add-update-photo-remove-${note.id}`}
                    onClick={removeUpdatePhoto}
                    className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-white/85 hover:bg-[#7A5A2E] hover:text-[#FBF8F1] text-[#2A2823] flex items-center justify-center shadow-sm"
                    title="Remove photo"
                  >
                    <X className="w-3.5 h-3.5" strokeWidth={2} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  data-testid={`add-update-photo-open-${note.id}`}
                  onClick={() => updatePhotoRef.current?.click()}
                  className="inline-flex items-center gap-1.5 text-xs text-[#4E5A3D] hover:text-[#3E4830] transition-colors"
                  title="Attach a photo showing how it's held up"
                >
                  <ImagePlus className="w-4 h-4" strokeWidth={1.75} />
                  {uploadingPhoto ? "uploading…" : "attach a photo (optional)"}
                </button>
              )}
              <input
                data-testid={`add-update-photo-input-${note.id}`}
                ref={updatePhotoRef}
                type="file"
                accept="image/*"
                onChange={pickUpdatePhoto}
                className="hidden"
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  data-testid={`add-update-cancel-${note.id}`}
                  onClick={() => { setDrafting(false); setText(""); setUpdatePhoto(null); }}
                  className="text-xs text-[#2A2823]/60 hover:text-[#2A2823] transition-colors px-2"
                >
                  Cancel
                </button>
                <Button
                  data-testid={`add-update-submit-${note.id}`}
                  onClick={submitUpdate}
                  disabled={busy || uploadingPhoto}
                  className="rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] h-9 px-4 shadow-none text-xs"
                >
                  Post update
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {canDelete && (
        <button
          data-testid={`delete-note-${note.id}`}
          onClick={onDelete}
          className="mt-4 inline-flex items-center gap-1.5 text-xs text-[#2A2823]/50 hover:text-[#4E5A3D] transition-colors no-print"
          title={isAuthor ? "Delete your Field Note" : "Owner moderation (abuse only)"}
        >
          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
          {isAuthor ? "delete" : "moderate"}
        </button>
      )}
      {user && !canDelete && (
        <button
          data-testid={`report-note-${note.id}`}
          onClick={onReport}
          className="mt-4 inline-flex items-center gap-1.5 text-xs text-[#2A2823]/40 hover:text-[#B79256] transition-colors no-print"
          title="Flag this note for the site owner (abuse only)"
        >
          <Flag className="w-3.5 h-3.5" strokeWidth={1.75} />
          flag as abusive
        </button>
      )}
      {lightboxSrc && (
        <div
          data-testid={`field-note-lightbox-${note.id}`}
          role="dialog"
          aria-modal="true"
          onClick={() => setLightboxSrc(null)}
          className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center p-6 cursor-zoom-out"
        >
          <img
            src={lightboxSrc}
            alt="Update photo enlarged"
            className="max-w-full max-h-full object-contain rounded-2xl shadow-xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            data-testid={`field-note-lightbox-close-${note.id}`}
            onClick={() => setLightboxSrc(null)}
            className="absolute top-5 right-5 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-[#2A2823] flex items-center justify-center shadow-lg"
            title="Close"
          >
            <X className="w-5 h-5" strokeWidth={2} />
          </button>
        </div>
      )}
    </article>
  );
}

const REPORT_PRESETS = [
  { value: "medical_claim", label: "Medical claim / treatment advice" },
  { value: "spam", label: "Spam or promotional post" },
  { value: "personal_attack", label: "Personal attack or harassment" },
  { value: "other", label: "Other (needs a short detail)" },
];

function TrustedReportButton({ ideaId }) {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState("medical_claim");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const needsDetails = preset === "other";

  const submit = async () => {
    if (needsDetails && !details.trim()) return toast("Add a short detail for 'other'.");
    setBusy(true);
    try {
      await api.post(`/ideas/${ideaId}/report`, {
        reason_preset: preset,
        details: details.trim() || null,
      });
      setDone(true);
      toast("Thanks — we'll take a look.");
    } catch (e) {
      toast(e?.response?.data?.detail || "Couldn't submit the report.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="mt-4 rounded-2xl border border-[#4E5A3D]/30 bg-[#4E5A3D]/8 px-4 py-3 text-sm text-[#4E5A3D]" data-testid="report-thanks">
        Thanks for flagging this. We'll review it soon.
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-2xl border border-[#B79256]/40 bg-[#B79256]/8 p-4" data-testid="trusted-report">
      <div className="flex items-start gap-3">
        <AlertOctagon className="w-4 h-4 text-[#B79256] mt-0.5 shrink-0" strokeWidth={1.75} />
        <div className="flex-1">
          <div className="text-sm text-[#2A2823]/80">
            <b>See something abusive, harassing, or a personal attack?</b>
            <br />
            A quick flag helps us keep Trusted honest and safe.
          </div>
          {!open ? (
            <button
              type="button"
              data-testid="report-branch-open"
              onClick={() => setOpen(true)}
              className="mt-3 inline-flex items-center gap-1.5 accent-label text-[#7A5A2E] hover:text-[#4E3A16] transition-colors"
            >
              <Flag className="w-3.5 h-3.5" strokeWidth={2} />
              Report this seed
            </button>
          ) : (
            <div className="mt-3 space-y-3">
              <div className="space-y-1.5" role="radiogroup" aria-label="Report reason">
                {REPORT_PRESETS.map((p) => (
                  <label
                    key={p.value}
                    data-testid={`report-preset-${p.value}`}
                    className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm cursor-pointer border transition-colors ${
                      preset === p.value
                        ? "bg-[#7A5A2E] text-[#FBF8F1] border-[#7A5A2E]"
                        : "bg-white/60 text-[#2A2823]/80 border-[#B79256]/30 hover:border-[#7A5A2E]"
                    }`}
                  >
                    <input
                      type="radio"
                      name={`preset-${ideaId}`}
                      value={p.value}
                      checked={preset === p.value}
                      onChange={() => setPreset(p.value)}
                      className="sr-only"
                    />
                    {p.label}
                  </label>
                ))}
              </div>
              <Textarea
                data-testid="report-branch-details"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder={needsDetails
                  ? "What feels off? (required for 'other')"
                  : "Optional: any extra detail — an example line or specific phrase."}
                className="rounded-2xl border-[#B79256]/40 bg-[#FBF8F1] text-sm"
                maxLength={500}
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  data-testid="report-branch-cancel"
                  onClick={() => { setOpen(false); setDetails(""); setPreset("medical_claim"); }}
                  className="text-xs text-[#2A2823]/60 hover:text-[#2A2823] transition-colors px-2"
                >
                  Cancel
                </button>
                <Button
                  data-testid="report-branch-submit"
                  onClick={submit}
                  disabled={busy}
                  className="rounded-full bg-[#7A5A2E] text-[#FBF8F1] hover:bg-[#5C441F] h-9 px-4 shadow-none text-xs"
                >
                  Submit report
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
